import {
  useQuery,
  gql,
  useMutation,
  useLazyQuery,
  QueryHookOptions,
  MutationHookOptions
} from '@apollo/client';
import {
  GQLQueryDeliveryUnit_GetDesignerCatalogForDeliveryUnitArgs,
  GQLQueryDeliveryUnit_GetPlatformProducerOrderArgs,
  GQLQueryDeliveryUnit_GetDeliveryUnitsByProducerArgs,
  GQLDeliveryUnitConnection,
  GQLQueryDeliveryUnit_GetDeliveryUnitAccessoryInventoryStatsArgs,
  GQLQueryDeliveryUnit_GetDeliveryUnitPieceInventoryStatsArgs,
  GQLQueryDeliveryUnit_GetAccessoryInventorysByPartyAndDeliveryUnitArgs,
  GQLAccessoryInventoryConnection,
  GQLDeliveryUnitPieceInventoryStats,
  GQLDeliveryUnitAccessoryInventoryStats,
  GQLQueryDeliveryUnit_GetAccessoryInventorysByAccessoryArgs,
  GQLQueryDeliveryUnit_GetPieceInventorysByPartyAndDeliveryUnitArgs,
  GQLPieceInventoryConnection,
  GQLQueryDeliveryUnit_GetPieceInventorysByPieceArgs,
  GQLQueryDeliveryUnit_GetPlatformProducerOrdersByProducerAndDeliveryUnitArgs,
  GQLPlatformProducerOrderConnection,
  GQLQueryDeliveryUnit_GetPlatformProducerOrderProductionPlanByPlatformProducerOrderIdArgs,
  GQLPlatformProducerOrderProductionPlan,
  GQLAccessoryInventory,
  GQLPieceInventory,
  GQLDeliveryUnit,
  GQLPlatformProducerOrder,
  GQLMutationDeliveryUnit_UpdatePieceInventoryArgs,
  GQLMutationDeliveryUnit_UpdateAccessoryInventoryArgs,
  GQLMutationDeliveryUnit_CreateMissingPieceInventorysArgs,
  GQLMutationDeliveryUnit_CreateMissingAccessoryInventorysArgs,
  GQLPlatformProducerOrderAndProductionPlan,
  GQLQueryDeliveryUnit_GetPlatformProducerOrderAndProductionPlanPreviewArgs,
  GQLMutationDeliveryUnit_UpdatePlatformProducerOrderStateArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { DELIVERY_UNIT_FIELDS } from './fragments/common';
import {
  PLATFORM_PRODUCER_ORDER_AND_PPRODUCTION_PLAN_FIELDS,
  PLATFORM_PRODUCER_ORDER_FIELDS,
  PLATFORM_PRODUCER_ORDER_PPRODUCTION_PLAN_FIELDS
} from './fragments/order';
import { ACCESSORY_INVENTORY_FIELDS, PIECE_INVENTORY_FIELDS } from './fragments/producer';
import { OperationsHelper } from './OperationHelper';

export class DeliveryUnitOperation {
  public static query = {
    DeliveryUnit_getDeliveryUnitsByProducer: gql`
      ${DELIVERY_UNIT_FIELDS}
      query DeliveryUnit_getDeliveryUnitsByProducer($producerPartyId: ID!) {
        DeliveryUnit_getDeliveryUnitsByProducer(producerPartyId: $producerPartyId) {
          deliveryUnits {
            ...DeliveryUnitFields
          }
        }
      }
    `
  };

  getPlatformProducerOrderAndProductionPlanFromResponse(
    data: any
  ): GQLPlatformProducerOrderAndProductionPlan {
    return data.DeliveryUnit_getPlatformProducerOrderAndProductionPlanPreview;
  }

  platformProducerOrderArrayFromResponse(data: any): GQLPlatformProducerOrder[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'platformProducerOrders');
  }

  platformProducerOrderFromResponse(data: any): GQLPlatformProducerOrder {
    return data.DeliveryUnit_getPlatformProducerOrder;
  }

  platformProducerOrderProductionPlanFromResponse(data: any): GQLPlatformProducerOrderProductionPlan {
    return data.DeliveryUnit_getPlatformProducerOrderProductionPlanByPlatformProducerOrderId;
  }

  deliveryUnitArrayFromResponse(data: any): GQLDeliveryUnit[] {
    return data.DeliveryUnit_getDeliveryUnitsByProducer.deliveryUnits;
    //return OperationsHelper.retrievePropertyFromGQLResponse(data, 'deliveryUnits');
  }

  pieceInventorysFromResponse(data: any): GQLPieceInventory[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'pieceInventorys');
  }

  accessoryInventorysFromResponse(data: any): GQLAccessoryInventory[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'accessoryInventorys');
  }

  designerCatalogFromResponse(data: any): string {
    return data.DeliveryUnit_getDesignerCatalogForDeliveryUnit;
  }

  pieceInventoryStatsFromResponse(data: any): GQLDeliveryUnitPieceInventoryStats {
    return data.DeliveryUnit_getDeliveryUnitPieceInventoryStats;
  }

  accessoryInventoryStatsFromResponse(data: any): GQLDeliveryUnitAccessoryInventoryStats {
    return data.DeliveryUnit_getDeliveryUnitAccessoryInventoryStats;
  }

  getDesignerCatalogForDeliveryUnit(
    args: GQLQueryDeliveryUnit_GetDesignerCatalogForDeliveryUnitArgs,
    options: QueryHookOptions<string, GQLQueryDeliveryUnit_GetDesignerCatalogForDeliveryUnitArgs> = {}
  ) {
    const query = gql`
      query getDesignerCatalogForDeliveryUnit($deliveryUnitId: ID!) {
        DeliveryUnit_getDesignerCatalogForDeliveryUnit(deliveryUnitId: $deliveryUnitId)
      }
    `;
    return useQuery<string, GQLQueryDeliveryUnit_GetDesignerCatalogForDeliveryUnitArgs>(query, {
      ...options,
      variables: args
    });
  }

  getDeliveryUnitAccessoryInventoryStats(
    args: GQLQueryDeliveryUnit_GetDeliveryUnitAccessoryInventoryStatsArgs,
    options: QueryHookOptions<
      GQLDeliveryUnitAccessoryInventoryStats,
      GQLQueryDeliveryUnit_GetDeliveryUnitAccessoryInventoryStatsArgs
    > = {}
  ) {
    const query = gql`
      query getDeliveryUnitAccessoryInventoryStatsQuery(
        $producerPartyId: ID!
        $deliveryUnitId: ID!
        $accessoryId: ID!
      ) {
        DeliveryUnit_getDeliveryUnitAccessoryInventoryStats(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          accessoryId: $accessoryId
        ) {
          existingAccessoryInventoryCount
          maximumAccessoryInventoryCount
        }
      }
    `;
    return useQuery<
      GQLDeliveryUnitAccessoryInventoryStats,
      GQLQueryDeliveryUnit_GetDeliveryUnitAccessoryInventoryStatsArgs
    >(query, { ...options, variables: args });
  }

  getDeliveryUnitPieceInventoryStats(
    args: GQLQueryDeliveryUnit_GetDeliveryUnitPieceInventoryStatsArgs,
    options: QueryHookOptions<
      GQLDeliveryUnitPieceInventoryStats,
      GQLQueryDeliveryUnit_GetDeliveryUnitPieceInventoryStatsArgs
    > = {}
  ) {
    const query = gql`
      query DeliveryUnit_getDeliveryUnitPieceInventoryStats(
        $producerPartyId: ID!
        $deliveryUnitId: ID!
        $pieceId: ID!
      ) {
        DeliveryUnit_getDeliveryUnitPieceInventoryStats(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          pieceId: $pieceId
        ) {
          existingPieceInventoryCount
          maximumPieceInventoryCount
        }
      }
    `;
    return useQuery<
      GQLDeliveryUnitPieceInventoryStats,
      GQLQueryDeliveryUnit_GetDeliveryUnitPieceInventoryStatsArgs
    >(query, { ...options, variables: args });
  }

  createMissingPieceInventorys(
    options: MutationHookOptions<String[], GQLMutationDeliveryUnit_CreateMissingPieceInventorysArgs> = {}
  ) {
    const mutation = gql`
      mutation createMissingPieceInventories($partyId: ID!, $deliveryUnitId: ID!, $pieceId: ID!) {
        DeliveryUnit_createMissingPieceInventorys(
          deliveryUnitId: $deliveryUnitId
          partyId: $partyId
          pieceId: $pieceId
        )
      }
    `;
    return useMutation<String[], GQLMutationDeliveryUnit_CreateMissingPieceInventorysArgs>(mutation, options);
  }

  createMissingAccessoryInventorys(
    options: MutationHookOptions<String[], GQLMutationDeliveryUnit_CreateMissingAccessoryInventorysArgs> = {}
  ) {
    const mutation = gql`
      mutation createMissingAccessoryInventories($partyId: ID!, $deliveryUnitId: ID!, $accessoryId: ID!) {
        DeliveryUnit_createMissingAccessoryInventorys(
          deliveryUnitId: $deliveryUnitId
          partyId: $partyId
          accessoryId: $accessoryId
        )
      }
    `;
    return useMutation<String[], GQLMutationDeliveryUnit_CreateMissingAccessoryInventorysArgs>(
      mutation,
      options
    );
  }

  updatePieceInventory(
    options: MutationHookOptions<String, GQLMutationDeliveryUnit_UpdatePieceInventoryArgs> = {}
  ) {
    const mutation = gql`
      mutation updatePiecesInventory($input: PieceInventoryUpdateInput!) {
        DeliveryUnit_updatePieceInventory(input: $input)
      }
    `;
    return useMutation<String, GQLMutationDeliveryUnit_UpdatePieceInventoryArgs>(mutation, options);
  }

  updateAccessoryInventory(
    options: MutationHookOptions<String, GQLMutationDeliveryUnit_UpdateAccessoryInventoryArgs> = {}
  ) {
    const mutation = gql`
      mutation updateAccessoriesInventory($input: AccessoryInventoryUpdateInput!) {
        DeliveryUnit_updateAccessoryInventory(input: $input)
      }
    `;
    return useMutation<String, GQLMutationDeliveryUnit_UpdateAccessoryInventoryArgs>(mutation, options);
  }

  getAccessoryInventorysByPartyAndDeliveryUnit(
    args: GQLQueryDeliveryUnit_GetAccessoryInventorysByPartyAndDeliveryUnitArgs,
    options: QueryHookOptions<
      GQLAccessoryInventoryConnection,
      GQLQueryDeliveryUnit_GetAccessoryInventorysByPartyAndDeliveryUnitArgs
    > = {}
  ) {
    const query = gql`
      ${ACCESSORY_INVENTORY_FIELDS}
      query getAccessoriesInventory($producerPartyId: ID!, $deliveryUnitId: ID!, $pageSize: Int = 1000) {
        DeliveryUnit_getAccessoryInventorysByPartyAndDeliveryUnit(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          pageSize: $pageSize
        ) {
          accessoryInventorys {
            ...AccessoryInventoryFields
          }
        }
      }
    `;

    return useQuery<
      GQLAccessoryInventoryConnection,
      GQLQueryDeliveryUnit_GetAccessoryInventorysByPartyAndDeliveryUnitArgs
    >(query, { ...options, variables: args });
  }

  getAccessoryInventorysByAccessory(
    args: GQLQueryDeliveryUnit_GetAccessoryInventorysByAccessoryArgs,
    options: QueryHookOptions<
      GQLAccessoryInventoryConnection,
      GQLQueryDeliveryUnit_GetAccessoryInventorysByAccessoryArgs
    > = {}
  ) {
    const query = gql`
      ${ACCESSORY_INVENTORY_FIELDS}
      query getAccessoriesInventory(
        $producerPartyId: ID!
        $deliveryUnitId: ID!
        $accessoryId: ID!
        $pageSize: Int = 1000
        $after: String
      ) {
        DeliveryUnit_getAccessoryInventorysByAccessory(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          accessoryId: $accessoryId
          pageSize: $pageSize
          after: $after
        ) {
          accessoryInventorys {
            ...AccessoryInventoryFields
          }
        }
      }
    `;

    return useQuery<
      GQLAccessoryInventoryConnection,
      GQLQueryDeliveryUnit_GetAccessoryInventorysByAccessoryArgs
    >(query, { ...options, variables: args });
  }

  getPieceInventorysByPartyAndDeliveryUnit(
    args: GQLQueryDeliveryUnit_GetPieceInventorysByPartyAndDeliveryUnitArgs,
    options: QueryHookOptions<
      GQLPieceInventoryConnection,
      GQLQueryDeliveryUnit_GetPieceInventorysByPartyAndDeliveryUnitArgs
    > = {}
  ) {
    const query = gql`
      ${PIECE_INVENTORY_FIELDS}
      query getPiecesInventory(
        $producerPartyId: ID!
        $deliveryUnitId: ID!
        $pageSize: Int = 1000
        $after: String
      ) {
        DeliveryUnit_getPieceInventorysByPartyAndDeliveryUnit(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          pageSize: $pageSize
          after: $after
        ) {
          pieceInventorys {
            ...PieceInventoryFields
          }
        }
      }
    `;

    return useQuery<
      GQLPieceInventoryConnection,
      GQLQueryDeliveryUnit_GetPieceInventorysByPartyAndDeliveryUnitArgs
    >(query, { ...options, variables: args });
  }

  getPieceInventorysByPiece(
    args: GQLQueryDeliveryUnit_GetPieceInventorysByPieceArgs,
    options: QueryHookOptions<
      GQLPieceInventoryConnection,
      GQLQueryDeliveryUnit_GetPieceInventorysByPieceArgs
    > = {}
  ) {
    const query = gql`
      ${PIECE_INVENTORY_FIELDS}
      query getPiecesInventory(
        $producerPartyId: ID!
        $deliveryUnitId: ID!
        $pieceId: ID!
        $pageSize: Int = 1000
        $after: String
      ) {
        DeliveryUnit_getPieceInventorysByPiece(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          pieceId: $pieceId
          pageSize: $pageSize
          after: $after
        ) {
          pieceInventorys {
            ...PieceInventoryFields
          }
        }
      }
    `;

    return useQuery<GQLPieceInventoryConnection, GQLQueryDeliveryUnit_GetPieceInventorysByPieceArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPlatformProducerOrder(
    args: GQLQueryDeliveryUnit_GetPlatformProducerOrderArgs,
    options: QueryHookOptions<
      GQLPlatformProducerOrder,
      GQLQueryDeliveryUnit_GetPlatformProducerOrderArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_PRODUCER_ORDER_FIELDS}
      query getPlatformProducerOrder($platformProducerOrderId: ID!) {
        DeliveryUnit_getPlatformProducerOrder(platformProducerOrderId: $platformProducerOrderId) {
          ...PlatformProducerOrderFields
        }
      }
    `;
    return useQuery<GQLPlatformProducerOrder, GQLQueryDeliveryUnit_GetPlatformProducerOrderArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPlatformProducerOrdersByProducerAndDeliveryUnit(
    args: GQLQueryDeliveryUnit_GetPlatformProducerOrdersByProducerAndDeliveryUnitArgs,
    options: QueryHookOptions<
      GQLPlatformProducerOrderConnection,
      GQLQueryDeliveryUnit_GetPlatformProducerOrdersByProducerAndDeliveryUnitArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_PRODUCER_ORDER_FIELDS}
      query getPlatformProducerOrdersByProducerAndDeliveryUnit(
        $producerPartyId: ID!
        $producerDeliveryUnitId: ID!
      ) {
        DeliveryUnit_getPlatformProducerOrdersByProducerAndDeliveryUnit(
          producerPartyId: $producerPartyId
          producerDeliveryUnitId: $producerDeliveryUnitId
        ) {
          cursor
          hasMore
          platformProducerOrders {
            ...PlatformProducerOrderFields
          }
        }
      }
    `;

    return useQuery<
      GQLPlatformProducerOrderConnection,
      GQLQueryDeliveryUnit_GetPlatformProducerOrdersByProducerAndDeliveryUnitArgs
    >(query, { ...options, variables: args });
  }

  getPlatformProducerOrderProductionPlanByPlatformProducerOrderId(
    args: GQLQueryDeliveryUnit_GetPlatformProducerOrderProductionPlanByPlatformProducerOrderIdArgs,
    options: QueryHookOptions<
      GQLPlatformProducerOrderProductionPlan,
      GQLQueryDeliveryUnit_GetPlatformProducerOrderProductionPlanByPlatformProducerOrderIdArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_PRODUCER_ORDER_PPRODUCTION_PLAN_FIELDS}
      query getPlatformProducerOrderProductionPlanByPlatformProducerOrderId($platformProducerOrderId: ID!) {
        DeliveryUnit_getPlatformProducerOrderProductionPlanByPlatformProducerOrderId(
          platformProducerOrderId: $platformProducerOrderId
        ) {
          ...PlatformProducerOrderProductionPlanFields
        }
      }
    `;

    return useQuery<
      GQLPlatformProducerOrderProductionPlan,
      GQLQueryDeliveryUnit_GetPlatformProducerOrderProductionPlanByPlatformProducerOrderIdArgs
    >(query, { ...options, variables: args });
  }

  getDeliveryUnitsByProducer(
    args: GQLQueryDeliveryUnit_GetDeliveryUnitsByProducerArgs,
    options: QueryHookOptions<
      GQLDeliveryUnitConnection,
      GQLQueryDeliveryUnit_GetDeliveryUnitsByProducerArgs
    > = {}
  ) {
    const query = gql`
      ${DELIVERY_UNIT_FIELDS}
      query DeliveryUnit_getDeliveryUnitsByProducer($producerPartyId: ID!) {
        DeliveryUnit_getDeliveryUnitsByProducer(producerPartyId: $producerPartyId) {
          deliveryUnits {
            ...DeliveryUnitFields
          }
        }
      }
    `;

    return useQuery<GQLDeliveryUnitConnection, GQLQueryDeliveryUnit_GetDeliveryUnitsByProducerArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPlatformProducerOrderAndProductionPlanPreview(
    args: GQLQueryDeliveryUnit_GetPlatformProducerOrderAndProductionPlanPreviewArgs,
    options: QueryHookOptions<
      GQLPlatformProducerOrderAndProductionPlan,
      GQLQueryDeliveryUnit_GetPlatformProducerOrderAndProductionPlanPreviewArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_PRODUCER_ORDER_AND_PPRODUCTION_PLAN_FIELDS}
      query getPlatformProducerOrderAndProductionPlanPreview(
        $producerPartyId: ID!
        $producerDeliveryUnitId: ID!
        $itemCustomisations: [Base64String!]!
      ) {
        DeliveryUnit_getPlatformProducerOrderAndProductionPlanPreview(
          producerPartyId: $producerPartyId
          producerDeliveryUnitId: $producerDeliveryUnitId
          itemCustomisations: $itemCustomisations
        ) {
          ...PlatformProducerOrderAndProductionPlanFields
        }
      }
    `;

    return useQuery<
      GQLPlatformProducerOrderAndProductionPlan,
      GQLQueryDeliveryUnit_GetPlatformProducerOrderAndProductionPlanPreviewArgs
    >(query, {
      ...options,
      variables: args
    });
  }

  updatePlatformProducerOrderState(
    options: MutationHookOptions<Boolean, GQLMutationDeliveryUnit_UpdatePlatformProducerOrderStateArgs> = {}
  ) {
    const mutation = gql`
      mutation updatePiecesInventory($id: ID!, $newState: PlatformProducerOrderState!, $note: String!) {
        DeliveryUnit_updatePlatformProducerOrderState(id: $id, newState: $newState, note: $note)
      }
    `;
    return useMutation<Boolean, GQLMutationDeliveryUnit_UpdatePlatformProducerOrderStateArgs>(
      mutation,
      options
    );
  }
}
